import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap, ArrowRight, X } from 'lucide-react';
import { useProfile } from '../context/ProfileContext';
import { calcTargets, sumLog, inferNutritionGoal } from '../data/nutrition';
import { localISO } from '../data/programs';

const DISMISS_KEY = 'enferno_macro_nudge_dismissed_v1';

/**
 * Smart fuel reminder. Renders only when the user is meaningfully behind on protein
 * for a strength day, after 3 PM. One tap → /nutrition.
 *
 * Conditions to render:
 *   - User has nutrition basics filled in (age / sex / height / weight)
 *   - Today is a strength workout day (not rest, not recovery)
 *   - Current hour ≥ 15 (3 PM)
 *   - Protein progress is below the time-of-day threshold:
 *       3–6 PM → require 40% of target
 *       ≥ 6 PM → require 65% of target
 *   - User hasn't dismissed the nudge today
 */
const MacroNudge = ({ workoutTitle, isRestDay }) => {
  const { profile } = useProfile();
  const today = localISO(new Date());
  const [dismissedToday, setDismissedToday] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(DISMISS_KEY) === today) setDismissedToday(true);
    } catch { /* ignore */ }
  }, [today]);

  // Hard exits
  if (isRestDay) return null;
  const hasBasics = profile.weight && profile.age && profile.sex && profile.heightFt;
  if (!hasBasics) return null;
  if (dismissedToday) return null;

  const hour = new Date().getHours();
  if (hour < 15) return null;

  // Compute today's protein gap
  const goalId = profile.nutritionGoal || inferNutritionGoal(profile.goals || []);
  const targets = calcTargets({ profile, goalId, activityId: profile.activityLevel || 'moderate' });
  if (!targets.proteinG) return null;
  const todayLog = (profile.foodLog && profile.foodLog[today]) || [];
  const totals = sumLog(todayLog);
  const proteinPct = totals.protein / targets.proteinG;
  const requiredPct = hour >= 18 ? 0.65 : 0.40;
  if (proteinPct >= requiredPct) return null;

  const proteinShort = Math.max(0, targets.proteinG - Math.round(totals.protein));
  const dayLabel = workoutTitle ? `your ${workoutTitle}` : 'today';
  const headline = hour >= 20
    ? `Last call: ${proteinShort}g protein short for ${dayLabel}.`
    : `You're ${proteinShort}g protein short for ${dayLabel}.`;

  const handleDismiss = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { localStorage.setItem(DISMISS_KEY, today); } catch { /* ignore */ }
    setDismissedToday(true);
  };

  return (
    <Link
      to="/nutrition"
      className="block relative bg-gradient-to-r from-enf-accent/15 via-zinc-950 to-enf-surface-special/30 border border-enf-accent-fg/40 rounded-xl p-5 mb-5 hover:border-enf-accent-fg transition-colors group"
      data-testid="macro-nudge"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-enf-text-muted hover:text-enf-text-primary p-1 transition-colors"
        aria-label="Dismiss for today"
        data-testid="macro-nudge-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-4 pr-8">
        <div className="w-10 h-10 rounded-md bg-enf-accent flex items-center justify-center flex-shrink-0">
          <Zap className="w-5 h-5 text-enf-text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-enf-accent-fg mb-1">Fuel check</div>
          <div className="font-display text-2xl md:text-3xl tracking-wide leading-tight mb-1.5">
            {headline}
          </div>
          <div className="text-sm text-enf-text-secondary">
            Grab a shake, eggs, or jerky. <span className="text-enf-text-primary font-semibold">{Math.round(totals.protein)}g / {targets.proteinG}g</span> logged so far · tap to log →
          </div>
        </div>
        <ArrowRight className="hidden md:block w-5 h-5 text-enf-accent-fg opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all flex-shrink-0 mt-2" />
      </div>
    </Link>
  );
};

export default MacroNudge;
